import { theme } from '@chakra-ui/react';
import type { ComponentStyleConfig } from '@chakra-ui/theme';
import { StyleFunctionProps } from '@chakra-ui/theme-tools';

const primary = (props: StyleFunctionProps) => ({
  ...theme.components.Button.variants?.solid(props),
  bg: 'blue.500',
  color: 'white',
  borderColor: 'transparent',
  _hover: {
    bg: ['blue.500', 'blue.200'],
    _disabled: {
      bg: 'blue.200',
      color: 'blue.50',
    },
  },
  _active: {
    bg: 'blue.800',
  },
  _disabled: {
    bg: 'blue.200',
    color: 'blue.50',
  },
});

const secondary = (props: StyleFunctionProps) => ({
  ...theme.components.Button.variants?.outline(props),
  bg: 'white',
  color: 'grey.900',
  borderColor: 'grey.200',
  _hover: {
    color: 'blue.500',
    borderColor: 'blue.500',
    _disabled: {
      bg: 'white',
      color: 'grey.300',
      borderColor: 'grey.200',
    },
  },
  _active: {
    bg: 'grey.50',
    color: 'blue.800',
    borderColor: 'blue.800',
  },
  _disabled: {
    bg: 'white',
    color: 'grey.300',
  },
});

const tertiary = (props: StyleFunctionProps) => ({
  ...theme.components.Button.variants?.outline(props),
  px: '5',
  py: '2',
  border: '0',
  minW: 'auto',
  color: 'blue.500',
  textDecoration: 'underline',
  textUnderlineOffset: 3,
  _hover: {
    color: 'blue.300',
    _disabled: {
      color: 'grey.300',
    },
  },
  _active: {
    color: 'blue.800',
  },
  _disabled: {
    color: 'grey.300',
  },
});

const negative = (props: StyleFunctionProps) => ({
  ...theme.components.Button.variants?.solid(props),
  bg: 'red.500',
  borderColor: 'red.500',
  color: 'white',
  _hover: {
    bg: 'red.300',
    borderColor: 'red.300',
    _disabled: {
      bg: 'red.200',
      color: 'red.50',
      borderColor: 'red.50',
    },
  },
  _active: {
    bg: 'red.800',
    borderColor: 'red.800',
  },
  _disabled: {
    bg: 'red.200',
    borderColor: 'red.50',
  },
});

const unstyled = (_props: StyleFunctionProps) => ({
  ...theme.components.Button.variants?.unstyled,
  borderStyle: 'none',
});

const link = () => ({
  ...theme.components.Button.variants?.link,
  borderStyle: 'none',
});

const ghost = () => ({
  ...theme.components.Button.variants?.ghost,
  borderStyle: 'none',
});

export const styleConfig: ComponentStyleConfig = {
  baseStyle: {
    fontWeight: 'bold',
    height: 'auto',
    minW: '40',
    border: '0.1',
    borderRadius: 'lg',
    _focusVisible: {
      outline: 0,
      boxShadow: '0 0 0 1px white, 0 0 0 3px #6691FF',
    },
  },
  sizes: {
    default: {
      py: '3',
      px: '6',
    },
    large: {
      fontSize: 'lg',
      py: '5',
      px: '8',
    },
  },
  variants: {
    primary,
    secondary,
    tertiary,
    negative,
    unstyled,
    link,
    ghost,
  },
  defaultProps: {
    variant: 'primary',
    size: 'default',
  },
};
